import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, CheckCheck, FileText, CheckCircle, XCircle, Info, X, ExternalLink, Calendar, Clock, History, Ban } from 'lucide-react'
import { Notification, NotificationType, EmployeeNote, LicenseRequest, REQUEST_TYPE_LABELS, REQUEST_STATUS_LABELS } from '../../types'
import { notificationService } from '../../services/notificationService'
import { noteService } from '../../services/noteService'
import { requestService } from '../../services/requestService'
import { useAuth } from '../../contexts/AuthContext'
import { formatDate } from '../../utils/dateUtils'

const notificationIcons: Record<NotificationType, React.ComponentType<any>> = {
  NEW_NOTE: FileText,
  NOTE_UPDATED: FileText,
  REQUEST_APPROVED: CheckCircle,
  REQUEST_DENIED: XCircle,
  REQUEST_CANCELLED: Ban,
  SYSTEM: Info,
}

const notificationColors: Record<NotificationType, string> = {
  NEW_NOTE: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  NOTE_UPDATED: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  REQUEST_APPROVED: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  REQUEST_DENIED: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  REQUEST_CANCELLED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  SYSTEM: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
}

const notificationTitles: Record<NotificationType, string> = {
  NEW_NOTE: 'Nueva Nota',
  NOTE_UPDATED: 'Nota Actualizada',
  REQUEST_APPROVED: 'Solicitud Aprobada',
  REQUEST_DENIED: 'Solicitud Denegada',
  REQUEST_CANCELLED: 'Solicitud Cancelada',
  SYSTEM: 'Aviso del Sistema',
}

export function NotificationBell() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedNote, setSelectedNote] = useState<EmployeeNote | null>(null)
  const [selectedRequest, setSelectedRequest] = useState<LicenseRequest | null>(null)
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Only show for employees
  const isEmployee = user?.role === 'EMPLOYEE'

  useEffect(() => {
    if (isEmployee) {
      fetchUnreadCount()
      const interval = setInterval(fetchUnreadCount, 30000) // Poll every 30s
      return () => clearInterval(interval)
    }
  }, [isEmployee])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close dropdown if modal is open
      if (isDetailModalOpen) return

      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDetailModalOpen])

  const fetchUnreadCount = async () => {
    try {
      const count = await notificationService.getUnreadCount()
      setUnreadCount(count)
    } catch (error) {
      console.error('Error fetching unread count:', error)
    }
  }

  const fetchNotifications = async () => {
    setIsLoading(true)
    try {
      const data = await notificationService.getAll()
      setNotifications(data)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications()
    }
    setIsOpen(!isOpen)
  }

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }

  const handleNotificationClick = async (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    setSelectedNotification(notification)
    setIsDetailModalOpen(true)
    setIsLoadingDetail(true)
    setSelectedNote(null)
    setSelectedRequest(null)

    try {
      // Load related data based on notification type
      if (notification.type === 'NEW_NOTE' || notification.type === 'NOTE_UPDATED') {
        if (notification.relatedId) {
          const note = await noteService.getById(notification.relatedId)
          setSelectedNote(note)
        }
      } else if (notification.type === 'REQUEST_APPROVED' || notification.type === 'REQUEST_DENIED' || notification.type === 'REQUEST_CANCELLED') {
        if (notification.relatedId) {
          const request = await requestService.getById(notification.relatedId)
          setSelectedRequest(request)
        }
      }

      // Mark as read if not already
      if (!notification.isRead) {
        await handleMarkAsRead(notification.id)
      }
    } catch (error) {
      console.error('Error loading notification details:', error)
    } finally {
      setIsLoadingDetail(false)
    }
  }

  const closeDetailModal = () => {
    setIsDetailModalOpen(false)
    setSelectedNotification(null)
    setSelectedNote(null)
    setSelectedRequest(null)
  }

  const handleViewAllNotifications = () => {
    setIsOpen(false)
    navigate('/notifications')
  }

  const formatRelativeDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Ahora'
    if (diffMins < 60) return `Hace ${diffMins} min`
    if (diffHours < 24) return `Hace ${diffHours}h`
    if (diffDays < 7) return `Hace ${diffDays}d`
    return formatDate(date)
  }

  if (!isEmployee) return null

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={handleToggle}
          className="relative p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 z-50">
            <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-slate-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">Notificaciones</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 flex items-center gap-1"
                >
                  <CheckCheck className="h-4 w-4" />
                  Marcar todas
                </button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay notificaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-slate-700">
                  {notifications.slice(0, 5).map((notification) => {
                    const Icon = notificationIcons[notification.type]
                    const colorClass = notificationColors[notification.type]

                    return (
                      <div
                        key={notification.id}
                        onClick={(e) => handleNotificationClick(notification, e)}
                        className={`p-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer ${
                          !notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full ${colorClass} flex items-center justify-center`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className={`text-sm text-gray-900 dark:text-white ${!notification.isRead ? 'font-medium' : ''}`}>
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {formatRelativeDate(notification.createdAt)}
                                </p>
                              </div>
                              {!notification.isRead && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleMarkAsRead(notification.id)
                                  }}
                                  className="flex-shrink-0 p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded text-gray-400 dark:text-gray-500"
                                  title="Marcar como leido"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Ver todas button */}
            <div className="p-2 border-t border-gray-200 dark:border-slate-700">
              <button
                onClick={handleViewAllNotifications}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
              >
                <History className="h-4 w-4" />
                Ver todas las notificaciones
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notification Detail Modal - Rendered via Portal */}
      {isDetailModalOpen && selectedNotification && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDetailModal()
          }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl min-h-[300px] max-h-[90vh] overflow-hidden flex flex-col animate-slide-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-5 border-b border-gray-200 dark:border-slate-700 ${
              selectedNotification.type === 'REQUEST_APPROVED' ? 'bg-green-50 dark:bg-green-900/20' :
              selectedNotification.type === 'REQUEST_DENIED' ? 'bg-red-50 dark:bg-red-900/20' :
              'bg-gray-50 dark:bg-slate-900/50'
            }`}>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {(() => {
                  const Icon = notificationIcons[selectedNotification.type]
                  const colorClass = selectedNotification.type === 'REQUEST_APPROVED' ? 'text-green-600' :
                                     selectedNotification.type === 'REQUEST_DENIED' ? 'text-red-600' :
                                     'text-primary-600'
                  return <Icon className={`h-5 w-5 ${colorClass}`} />
                })()}
                {notificationTitles[selectedNotification.type]}
              </h2>
              <button
                onClick={closeDetailModal}
                className="p-2 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {isLoadingDetail ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Basic notification info */}
                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 dark:border-slate-600 dark:bg-slate-700/30">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-2">
                      {selectedNotification.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedNotification.message}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDate(selectedNotification.createdAt)}
                    </p>
                  </div>

                  {/* Note-specific content */}
                  {(selectedNotification.type === 'NEW_NOTE' || selectedNotification.type === 'NOTE_UPDATED') && selectedNote && (
                    <div className={`p-5 rounded-xl border-2 ${
                      selectedNote.isImportant
                        ? 'border-yellow-400 bg-yellow-50 dark:border-yellow-500 dark:bg-yellow-900/20'
                        : 'border-blue-200 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Contenido de la Nota</span>
                        {selectedNote.isImportant && (
                          <span className="badge badge-warning text-xs">Importante</span>
                        )}
                      </div>
                      <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-2">
                        {selectedNote.title}
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {selectedNote.content}
                      </p>
                      <div className="mt-4 pt-3 border-t border-blue-200 dark:border-blue-600">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Creada por <span className="font-semibold">{selectedNote.createdBy.name}</span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Request-specific content */}
                  {(selectedNotification.type === 'REQUEST_APPROVED' || selectedNotification.type === 'REQUEST_DENIED' || selectedNotification.type === 'REQUEST_CANCELLED') && selectedRequest && (
                    <div className={`p-5 rounded-xl border-2 ${
                      selectedNotification.type === 'REQUEST_APPROVED'
                        ? 'border-green-300 bg-green-50 dark:border-green-600 dark:bg-green-900/20'
                        : selectedNotification.type === 'REQUEST_CANCELLED'
                        ? 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/20'
                        : 'border-red-300 bg-red-50 dark:border-red-600 dark:bg-red-900/20'
                    }`}>
                      <div className="flex items-center gap-3 mb-4">
                        <Calendar className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Detalles de la Solicitud</span>
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Tipo de solicitud:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {REQUEST_TYPE_LABELS[selectedRequest.requestType]}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Periodo:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatDate(selectedRequest.startDate)}
                            {selectedRequest.secondDate
                              ? ` y ${formatDate(selectedRequest.secondDate)}`
                              : selectedRequest.startDate !== selectedRequest.endDate
                              ? ` - ${formatDate(selectedRequest.endDate)}`
                              : ''}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Dias solicitados:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {selectedRequest.daysRequested} dias
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Estado:</span>
                          <span className={`badge ${
                            selectedRequest.status === 'APPROVED' ? 'badge-success' :
                            selectedRequest.status === 'DENIED' ? 'badge-error' :
                            selectedRequest.status === 'CANCELLED' ? 'badge-gray' : 'badge-warning'
                          }`}>
                            {REQUEST_STATUS_LABELS[selectedRequest.status]}
                          </span>
                        </div>
                        {selectedRequest.denialReason && (
                          <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-600">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Motivo de la denegacion:</p>
                            <p className="text-red-700 dark:text-red-300 font-medium">
                              {selectedRequest.denialReason}
                            </p>
                          </div>
                        )}
                        {selectedRequest.cancellationReason && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Motivo de la cancelacion:</p>
                            <p className="text-gray-700 dark:text-gray-300 font-medium">
                              {selectedRequest.cancellationReason}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* View request button */}
                      <button
                        onClick={() => {
                          closeDetailModal()
                          navigate(`/requests/${selectedRequest.id}`)
                        }}
                        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-300 dark:border-primary-600 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Ver solicitud completa
                      </button>
                    </div>
                  )}

                  {/* System notification */}
                  {selectedNotification.type === 'SYSTEM' && (
                    <div className="p-4 rounded-xl border border-gray-300 bg-gray-100 dark:border-slate-600 dark:bg-slate-700">
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Aviso del Sistema</span>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400">
                        Este es un mensaje informativo del sistema.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50">
              <button
                onClick={closeDetailModal}
                className="w-full btn btn-primary py-3 text-base font-semibold"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
